<template>
  <div class="course-history-pagination">
    <div
      class="course-history-pagination__label course-history-pagination__label--showing"
    >
      {{ showingLabel }}
    </div>
    <div class="course-history-pagination__select-container">
      <div class="course-history-pagination__label">Results per page</div>
      <select
        @change="setPageItems($event)"
        class="course-history-pagination__select"
        name="pageItems"
      >
        <option value="10" selected>10</option>
        <option value="25">25</option>
        <option value="50">50</option>
        <option value="100">100</option>
        <option value="250">150</option>
      </select>
    </div>
    <div class="course-history-pagination__wrapper">
      <ChevronLeftIcon
        v-if="currentPage !== 1"
        @click="setCurrentPage(currentPage - 1)"
        :class="[
          'course-history-pagination__page-nav-btn',
          currentPage === 1 &&
            'course-history-pagination__page-nav-btn--disabled',
        ]"
      ></ChevronLeftIcon>
      <button
        v-for="currentPageLabel in currentPages"
        @click="setCurrentPage(currentPageLabel + 1)"
        :key="currentPageLabel"
        :class="[
          'course-history-pagination__page-btn',
          currentPageLabel + 1 === currentPage &&
            'course-history-pagination__page-btn--selected',
        ]"
      >
        {{ currentPageLabel + 1 }}
      </button>
      <button
        v-if="showDots"
        @click="setCurrentPage(currentLastPage + 1)"
        class="course-history-pagination__page-btn course-history-pagination__page-dots"
      >
        ...
      </button>
      <ChevronRightIcon
        @click="setCurrentPage(currentPage + 1)"
        :class="[
          'course-history-pagination__page-nav-btn',
          currentPage === totalPages &&
            'course-history-pagination__page-nav-btn--disabled',
        ]"
      ></ChevronRightIcon>
    </div>
  </div>
</template>

<script>
import { ChevronRightIcon, ChevronLeftIcon } from 'vue-feather-icons'

export default {
  name: 'CourseHistoryPagination',
  components: {
    ChevronRightIcon,
    ChevronLeftIcon,
  },
  props: {
    courses: {
      type: Array,
      required: true,
    },
    totalItems: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 0,
      currentPages: [],
      itemsPerPage: 10,
      maxPagesShowed: 4,
    }
  },
  computed: {
    showingLabel() {
      const offset = this.itemsPerPage
      const firstItem =
        this.currentPage === 1 ? 1 : (this.currentPage - 1) * offset + 1
      const lastPageItem = this.currentPage * offset
      const lastTotalItem = this.totalItems
      const lastItem =
        lastPageItem < lastTotalItem ? this.currentPage * offset : lastTotalItem
      return `Showing ${firstItem}-${lastItem} results out of ${lastTotalItem}`
    },
    showDots() {
      const maxPagesShowed = this.maxPagesShowed
      return (
        this.currentPages?.length === maxPagesShowed &&
        maxPagesShowed !== this.totalPages
      )
    },
  },
  methods: {
    createCoursesByPages() {
      const itemsPerPage = this.itemsPerPage
      const currentPageCount = this.currentPage
      const maxPagesShowed = this.maxPagesShowed
      const totalCourses = this.totalItems
      const totalPages = Math.ceil(totalCourses / itemsPerPage)
      this.totalPages = totalPages
      let currentLastPage = itemsPerPage
      let currentFirstPage = 1
      if (currentPageCount / maxPagesShowed > 1)
        currentFirstPage =
          Math.floor(totalPages / currentPageCount) * maxPagesShowed + 1
      currentLastPage =
        currentFirstPage + this.maxPagesShowed - 1 <= totalPages
          ? currentFirstPage + this.maxPagesShowed - 1
          : totalPages
      const currentPages = []
      for (let n = currentFirstPage; n <= currentLastPage; n++) {
        currentPages.push(n - 1)
      }
      this.currentPages = currentPages
    },
    updateCoursesPagination() {
      this.createCoursesByPages()
    },
    setCurrentPage(pageNum) {
      this.currentPage = pageNum
      this.checkCurrentPages(pageNum)
      this.$parent.$emit(
        'update-current-page-number',
        this.itemsPerPage,
        pageNum
      )
    },
    setPageItems(event) {
      const newItemsPerPage = Number(event.target.value)
      const currentPage =
        Math.floor(
          (this.currentPage * Math.min(this.itemsPerPage, this.totalItems)) /
            newItemsPerPage
        ) || 1
      this.currentPage = Math.min(currentPage, this.totalItems)
      this.itemsPerPage = newItemsPerPage
      this.$parent.$emit(
        'update-current-page-items',
        newItemsPerPage,
        this.currentPage
      )
      this.createCoursesByPages()
    },
    checkCurrentPages(pageNum) {
      if (!this.currentPages.includes(pageNum - 1)) this.createCoursesByPages()
    },
  },
  created() {
    this.createCoursesByPages()
  },
}
</script>

<style lang="scss">
.course-history-pagination {
  display: flex;
  flex-direction: column;
  margin: 20px 0 0 0;
  align-items: center;
  color: var(--white);
  font-size: rem(14);
  line-height: 20px;
  @include mobile {
    flex-direction: row;
  }
  &__label {
    @include mobile {
      order: 1;
    }
    &--showing {
      margin: 0 0 10px 0;
      @include mobile {
        margin: 0 0 0 auto;
        order: 3;
      }
    }
  }
  &__select {
    height: 44px;
    width: 61px;
    margin-left: 22px;
    border-radius: 10px;
    padding: 5px;
    @include mobile {
      order: 2;
    }
    &-container {
      display: flex;
      align-items: center;
      margin-bottom: 10px;
      @include mobile {
        margin-bottom: 0;
      }
    }
  }

  &__wrapper {
    display: flex;
    align-items: center;
    @include mobile {
      order: 4;
      margin-left: 26px;
    }
  }
  &__page-btn,
  &__page-nav-btn {
    border: none;
    background: transparent;
    color: var(--chetwode-blue);
    cursor: pointer;
    font-family: var(--font-bold);
    &--disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }
  &__page-btn {
    margin: 0 5px;
    border-radius: 8px;
    padding: 8px 13px;
    max-width: 32px;
    width: 32px;
    color: var(--light-blue-gray);
    &--selected,
    &:hover {
      color: var(--white);
      background: var(--butterfly-bush);
    }
  }
  &__page-dots {
    padding: 8px;
    font-size: rem(18px);
  }
}
</style>
